import React, { useState } from "react";
import { useMutation, useSubscription } from "@apollo/client";
import { Alert, AlertTitle, Button, Skeleton, Typography } from "@mui/material";

import {
  DealYardiJobDocument,
  ResendYardiJobDocument,
} from "../../generated/graphql-operations";
import { Role, useReturnUserRole } from "../../contexts/Auth/UserRole";
import { describeYardiJob } from "./yardiJobDisplay";

const RESEND_ROLES: string[] = [Role.Admin];

export const YardiJobStatus = (props: { dealId: number }) => {
  const role = useReturnUserRole();
  const { data, loading, error } = useSubscription(DealYardiJobDocument, {
    variables: { deal_id: props.dealId },
  });
  const [resend, { loading: sending, error: sendError, data: sendData }] =
    useMutation(ResendYardiJobDocument);
  // The row stays failed for a moment after the click, so remember which failure was already sent
  const [sentFor, setSentFor] = useState<string | null>(null);

  if (loading) return <Skeleton width="100%" />;
  if (error) {
    return (
      <Typography variant="body2" sx={{ p: 2 }}>
        The Yardi job status could not be loaded.
      </Typography>
    );
  }

  const row = data?.YardiJobs?.[0];
  const job = describeYardiJob(row);
  const alreadySent = sentFor !== null && sentFor === row?.modified_at;

  const sendAgain = async () => {
    const result = await resend({
      variables: { dealId: props.dealId },
    }).catch(() => undefined);
    if (result?.data?.resendYardiJob) setSentFor(row?.modified_at ?? null);
  };

  return (
    <Alert
      severity={job.severity}
      sx={{ width: "100%", m: 2 }}
      action={
        job.canResend && RESEND_ROLES.includes(role) ? (
          <Button
            color="inherit"
            size="small"
            onClick={sendAgain}
            disabled={sending || alreadySent}
          >
            Send to Yardi again
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{job.label}</AlertTitle>
      {job.jobCode && (
        <Typography variant="body2">Job code: {job.jobCode}</Typography>
      )}
      {job.lastAttempt && (
        <Typography variant="body2">Last attempt: {job.lastAttempt}</Typography>
      )}
      {job.message && <Typography variant="body2">{job.message}</Typography>}
      {alreadySent && sendData?.resendYardiJob?.message && (
        <Typography variant="body2">
          {sendData.resendYardiJob.message}
        </Typography>
      )}
      {sendError && (
        <Typography variant="body2">{sendError.message}</Typography>
      )}
    </Alert>
  );
};
